import { Ban, Clock, Footprints, MapPin, Moon, Mountain, Ship, Sun } from "lucide-react";
import { formatTime } from "../lib/format";
import { dictionaries } from "../lib/i18n";
import type { DayPlan, FerryCrossing, TrafficAlert, UiLang } from "../lib/types";

/**
 * Det dagen ikke kan planlegges rundt — øverst, før stoppene.
 *
 * Alle tre opplysningene fantes fra før, og alle tre sto feil sted. Fotturen
 * var en linje i stoppdetaljene, tre klikk unna. Ferjene lå i én samlet liste
 * nederst på siden, like langt fra dag 2 som fra dag 6. Veimeldingene lå i en
 * fjerde liste, sortert etter alvorlighet framfor etter hvilken dag man kjører
 * strekningen.
 *
 * Felles for dem er at de ikke er egenskaper ved *ruta*, men beskrankninger på
 * én bestemt dag: en tolv timers tur, en avgang klokka 16:20, en stengt
 * fjellovergang. Den som leser dag 4 må se dem der, ellers oppdages de ikke
 * før man står i det.
 *
 * Blokka vises bare når dagen faktisk har en beskrankning. En tom ramme med
 * «ingen hindringer» ville lært leseren å hoppe over den.
 */

interface Props {
  day: DayPlan;
  /** Ferjene på hele turen. Filtreres på dagnummer her. */
  ferries: FerryCrossing[];
  /** Veimeldingene på hele turen. Filtreres på dagnummer her. */
  alerts: TrafficAlert[];
  lang: UiLang;
}

export function DayConstraints({ day, ferries, alerts, lang }: Props) {
  const t = dictionaries[lang];

  const todaysFerries = ferries.filter((crossing) => crossing.dayNumber === day.dayNumber);

  /*
   * Bare det som faktisk stopper deg. Veiarbeid er 974 av 1247 meldinger i
   * Norge, og et varsel som alltid står der leses ikke — mens en stengt
   * fjellovergang er hele grunnen til at man skulle lest det.
   */
  const blocking = alerts.filter(
    (alert) =>
      alert.dayNumber === day.dayNumber &&
      (alert.severity === "closed" || alert.severity === "convoy"),
  );

  if (!day.fullDayHike && todaysFerries.length === 0 && blocking.length === 0) return null;

  return (
    <div className="constraints">
      {/*
        Fotturen først. Den er ikke et stopp på dagen — den er dagen, og
        inndelingen på serveren holder dagen fri for den.
      */}
      {day.fullDayHike && (
        <div className="constraint constraint--hike">
          <span className="constraint-icon">
            <Footprints size={16} />
          </span>
          <div className="constraint-body">
            <div className="constraint-head">
              <strong>{day.fullDayHike.name}</strong>
              <span className="constraint-tag">{t.constraintFullDay}</span>
            </div>
            <div className="constraint-facts">
              <span>
                <Clock size={12} />
                {t.hikeRoundTrip(day.fullDayHike.hoursMin, day.fullDayHike.hoursMax)}
              </span>
              <span>
                <MapPin size={12} />
                {day.fullDayHike.trailhead}
              </span>
              {/*
                Rekker dagslyset til turen? Den skarpeste opplysningen appen
                har om terreng og tid sammen, og den fantes ikke før.
              */}
              {day.fullDayHike.daylightHours !== undefined && (
                <span className={day.fullDayHike.fitsDaylight ? "" : "constraint-fact--warn"}>
                  {day.fullDayHike.fitsDaylight ? <Sun size={12} /> : <Moon size={12} />}
                  {day.fullDayHike.fitsDaylight
                    ? t.constraintDaylightFits(day.fullDayHike.daylightHours)
                    : t.constraintDaylightShort(day.fullDayHike.daylightHours)}
                </span>
              )}
            </div>
            <p className="constraint-note">{t.constraintHikeOwnsDay}</p>
          </div>
        </div>
      )}

      {todaysFerries.map((crossing) => {
        const next = crossing.departures.find((departure) => !departure.cancelled);
        return (
          <div className="constraint constraint--ferry" key={`${crossing.stopId}-${crossing.toName}`}>
            <span className="constraint-icon">
              <Ship size={16} />
            </span>
            <div className="constraint-body">
              <div className="constraint-head">
                <strong>
                  {crossing.quayName} → {crossing.toName}
                </strong>
                <span className="constraint-tag">{t.constraintFerry}</span>
              </div>
              <div className="constraint-facts">
                {/*
                  Avgangene vi har, ikke «siste avgang»: Vegvesenet spørres om
                  de nærmeste fire, og å kalle den siste av dem dagens siste
                  ville vært å påstå noe vi ikke har hentet.
                */}
                {crossing.departures.slice(0, 4).map((departure) => (
                  <span
                    className={departure.cancelled ? "constraint-fact--warn" : ""}
                    key={departure.departure}
                  >
                    {formatTime(departure.departure, lang)}
                    {departure.cancelled && ` · ${t.ferryCancelled}`}
                  </span>
                ))}
              </div>
              <p className="constraint-note">
                {next ? t.constraintFerryMissed(formatTime(next.departure, lang)) : t.constraintFerryNone}
              </p>
            </div>
          </div>
        );
      })}

      {blocking.map((alert) => (
        <div className={`constraint constraint--${alert.severity}`} key={alert.id}>
          <span className="constraint-icon">
            {alert.severity === "convoy" ? <Clock size={16} /> : <Ban size={16} />}
          </span>
          <div className="constraint-body">
            <div className="constraint-head">
              <strong>
                {alert.road && <span className="constraint-road">{alert.road}</span>}
                {alert.location}
              </strong>
              <span className="constraint-tag">{t.trafficSeverity[alert.severity]}</span>
              {alert.onMountainPass && (
                <span className="constraint-tag constraint-tag--pass">
                  <Mountain size={10} /> {t.trafficMountainPass}
                </span>
              )}
            </div>
            <p className="constraint-note">{alert.description}</p>
            {alert.onLeg && (
              <p className="constraint-note constraint-note--leg">
                {t.constraintOnTodaysLeg(alert.onLeg.fromName, alert.onLeg.toName)}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
